import { FC, useRef, useState } from 'react';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { Nullable } from 'utils/tsHelpers';
import {
  CopyAssistContextState,
  CopyAssistFormAttributes,
  CopyAssistFormState,
  VariantFormFieldKey,
} from '../types';
import { CopyAssistContext } from './CopyAssistContext';
import { useTakeoverExperimentContext } from './TakeoverExperimentContext';

export const CopyAssistContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [activatedFormField, setActivatedFormField] = useState<Nullable<VariantFormFieldKey>>(null);
  const [copyAssistFormState, setCopyAssistFormState] =
    useState<Nullable<CopyAssistFormState>>(null);
  const {
    overlay: { isOverlayEnable },
  } = useTakeoverExperimentContext();

  const copyAssistFormAttributes: CopyAssistFormAttributes = {
    formFieldWrapperRef: useRef<HTMLDivElement>(null),
    formFieldRef: useRef<HTMLDivElement>(null),
  };

  // This will open the copy assist dropdown module on the
  // fieldName which matches the passed argument
  const activateCopyAssist = (fieldName: VariantFormFieldKey, key: string) => {
    setActivatedFormField(fieldName);
    setCopyAssistFormState({
      [key]: copyAssistFormAttributes,
    });
  };

  const closeCopyAssist = () => {
    setActivatedFormField(null);
    setCopyAssistFormState(null);
  };

  useClickOutside(copyAssistFormAttributes.formFieldWrapperRef, () => {
    // The overlay is set to true when the copy assist module is adding
    // the suggestions as variants. So, we don't want to close the CA
    // dropdown when the user clicks outside while we are adding variants.
    if (!isOverlayEnable) {
      closeCopyAssist();
    }
  });

  const context: CopyAssistContextState = {
    activatedFormField,
    copyAssistFormState,
    activateCopyAssist,
    closeCopyAssist,
  };

  return <CopyAssistContext.Provider value={context}>{children}</CopyAssistContext.Provider>;
};
